import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { bookingApi } from '../services/api';

export interface CalendarBooking {
  id: string;
  customerId: string;
  storeId: string;
  serviceId: string;
  staffId?: string | null;
  date: string;
  price: number;
  duration: number;
  status: 'PENDING' | 'CONFIRMED' | 'CANCELLED' | 'COMPLETED';
  managementToken: string;
  note?: string | null;
  customer: {
    id: string;
    name: string;
    phone?: string | null;
    email?: string | null;
  };
  service: {
    id: string;
    name: string;
    description?: string | null;
    price: number;
    duration: number;
    category?: string | null;
  };
  staff?: {
    id: string;
    name: string;
    phone?: string | null;
    email?: string | null;
  } | null;
}

type ViewMode = 'day' | 'week' | 'staff';

interface CalendarState {
  calendarBookings: CalendarBooking[];
  isLoading: boolean;
  hasFetched: boolean;
  error: string | null;
  viewMode: ViewMode;
  selectedDate: string; // ISO string（不 persist，每次開啟都從今天開始）
  lastFetchTime: number | null;
  fetchedRange: { start: string; end: string } | null; // 快取資料涵蓋的日期範圍

  // Actions
  fetchCalendarData: (storeId: string, selectedDate: Date, forceRefresh?: boolean) => Promise<void>;
  setViewMode: (mode: ViewMode) => void;
  setSelectedDate: (date: Date) => void;
  refreshCalendarData: (storeId: string, selectedDate: Date) => Promise<void>;
  updateLocalBooking: (bookingId: string, updatedData: Partial<CalendarBooking>) => void;
  addLocalBooking: (booking: CalendarBooking) => void;
}

export const useCalendarStore = create<CalendarState>()(
  persist(
    (set, get) => ({
      calendarBookings: [],
      isLoading: false,
      hasFetched: false,
      error: null,
      viewMode: 'day',
      selectedDate: new Date().toISOString(),
      lastFetchTime: null,
      fetchedRange: null,

      fetchCalendarData: async (storeId: string, selectedDate: Date, forceRefresh = false) => {
        const state = get();
        const CACHE_DURATION = 2 * 60 * 60 * 1000; // 2 小時快取

        // 強制刷新時直接獲取
        if (forceRefresh) {
          set({ isLoading: true, error: null });
        } else {
          // 檢查快取是否仍然有效（時間未過期，且涵蓋目前選取的日期）
          const now = Date.now();
          const target = selectedDate.getTime();
          const coversDate = !!state.fetchedRange &&
                             target >= new Date(state.fetchedRange.start).getTime() &&
                             target <= new Date(state.fetchedRange.end).getTime();
          const isCacheValid = coversDate &&
                              !!state.lastFetchTime &&
                              (now - state.lastFetchTime) < CACHE_DURATION;

          if (isCacheValid) {
            // 快取仍然有效，不需要重新載入
            return;
          }

          // 只有在首次讀取且無資料時才顯示載入狀態
          const shouldShowLoading = state.calendarBookings.length === 0 && !state.hasFetched;
          if (shouldShowLoading) {
            set({ isLoading: true, error: null });
          }
        }

        try {
          // 載入前後 30 天的資料
          const startDate = new Date(selectedDate);
          startDate.setDate(startDate.getDate() - 30);
          const endDate = new Date(selectedDate);
          endDate.setDate(endDate.getDate() + 30);

          const response = await bookingApi.list({
            storeId,
            startDate: startDate.toISOString(),
            endDate: endDate.toISOString(),
          });

          const data = response.data || [];
          set({
            calendarBookings: data,
            isLoading: false,
            hasFetched: true,
            lastFetchTime: Date.now(),
            fetchedRange: { start: startDate.toISOString(), end: endDate.toISOString() },
          });
        } catch (error: any) {
          console.error('Failed to load calendar data:', error);
          set({
            error: error.message || '無法載入行事曆資料',
            isLoading: false,
            hasFetched: true,
          });
        }
      },

      setViewMode: (mode: ViewMode) => {
        set({ viewMode: mode });
      },

      setSelectedDate: (date: Date) => {
        set({ selectedDate: date.toISOString() });
      },

      refreshCalendarData: async (storeId: string, selectedDate: Date) => {
        const { fetchCalendarData } = get();
        await fetchCalendarData(storeId, selectedDate, true);
      },

      updateLocalBooking: (bookingId: string, updatedData: Partial<CalendarBooking>) => {
        set((state) => ({
          calendarBookings: state.calendarBookings.map((booking) =>
            booking.id === bookingId ? { ...booking, ...updatedData } : booking
          ),
        }));
      },

      addLocalBooking: (booking: CalendarBooking) => {
        set((state) => ({
          calendarBookings: [...state.calendarBookings, booking],
        }));
      },
    }),
    {
      name: 'calendar-storage',
      version: 1,
      // v0 曾 persist selectedDate，導致日曆打開停在舊日期；升版時丟掉舊值與無範圍資訊的快取
      migrate: (persisted: any) => {
        const rest = { ...(persisted || {}) };
        delete rest.selectedDate;
        return { ...rest, lastFetchTime: null, fetchedRange: null };
      },
      partialize: (state) => ({
        calendarBookings: state.calendarBookings,
        hasFetched: state.hasFetched,
        viewMode: state.viewMode,
        lastFetchTime: state.lastFetchTime,
        fetchedRange: state.fetchedRange,
      }),
    }
  )
);
